import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { IUser } from '@core/models';
import { Authentication, Browser } from '@core/services';
import { NgbPopover, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { LoginForm } from 'src/app/features/user/components/login-form/login-form';

/**
 * Maximum number of characters to display for the username
 */
const NUM_CHAR_USERNAME = 7;

/**
 * Login button component that handles user authentication display and login form popover.
 * Displays user information when authenticated and provides login form when not authenticated.
 */
@Component({
  selector: 'app-login-button',
  imports: [LoginForm, NgbPopoverModule, TranslateModule, RouterModule],
  templateUrl: './login-button.html',
  styleUrl: './login-button.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginButton {
  private readonly authService = inject(Authentication);
  private readonly browser = inject(Browser);

  /**
   * Stores the user authentication information.
   */
  protected readonly auth = signal<IUser | null>(null);

  /**
   * Reference to the popover instance in the template.
   */
  private readonly popover = viewChild<NgbPopover>('p');

  /**
   * Indicates whether the current viewport is mobile size.
   */
  protected readonly isMobile = signal<boolean>(
    this.browser.isBrowser ? window.innerWidth <= 991 : false,
  );

  /**
   * Computes the username to display, truncating if longer than allowed characters.
   */
  protected readonly userName = computed<string>(() => {
    const user = this.auth();
    if (!user) return '';
    const userName = user.username || '';
    return userName.length < NUM_CHAR_USERNAME
      ? userName
      : `${userName.substring(0, NUM_CHAR_USERNAME)}...`;
  });

  /**
   * Initializes the component and sets up authentication subscriptions.
   * Subscribes to authentication events, logout events, and login form opening requests.
   */
  constructor() {
    this.getAuth();
    this.authService.onAuthenticate.pipe(takeUntilDestroyed()).subscribe(() => this.getAuth());

    this.authService.onLogout.pipe(takeUntilDestroyed()).subscribe(() => this.auth.set(null));

    this.authService.openLoginForm.pipe(takeUntilDestroyed()).subscribe(() => {
      this.popover()?.open();
    });
  }

  /**
   * Retrieves and stores the current user authentication information.
   */
  private getAuth(): void {
    this.auth.set(this.authService.getAuthInformation());
  }

  /**
   * Logs out the user and closes the popover.
   * @param popover - The popover instance to close
   */
  protected logout(popover: NgbPopover): void {
    this.authService.logout();
    popover.close();
  }

  /**
   * Closes the popover.
   * @param a - The popover instance to close
   */
  closeLogout(a: NgbPopover): void {
    a.close();
  }
}
